import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CreditCard } from '@mui/icons-material';
import { CardElement } from '@stripe/react-stripe-js';

import { EPaymentMethod } from 'enums';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { getPaymentTypeString } from 'utils';

import { usePaymentMethodFormManager } from './payment-method-form.hook';

interface PaymentMethodFormProps {
  onLoading?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const PaymentMethodForm = React.forwardRef(({ onLoading = () => {}, next }: PaymentMethodFormProps, ref) => {
  const {
    paymentMethod,
    nameOnPayment: name,
    billingEmail: email,
    cardLastFourCode,
  } = useAppSelector(selectCurrentUserInfo);

  const [formManager, paymentElementError, { isUpdating }] = usePaymentMethodFormManager({ next });
  React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
  React.useEffect(() => onLoading(isUpdating), [isUpdating]);

  const [showCardElement, setShowCardElement] = React.useState<boolean>(false);
  const theme = useTheme();

  React.useEffect(() => {
    formManager.setValues({
      paymentMethod,
      name,
      email,
      cardLastFourCode,
    });
  }, [paymentMethod, name, email, cardLastFourCode]);

  React.useEffect(() => {
    if (formManager.values.paymentMethod === EPaymentMethod.INVOICE) {
      setShowCardElement(false);

      formManager.setFieldValue('email', email);
    }

    if (formManager.values.paymentMethod === EPaymentMethod.CARD) {
      const isCard = !!formManager.values.cardLastFourCode;

      setShowCardElement(!isCard);
    }
  }, [formManager.values.paymentMethod]);

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        lineHeight: '27px',
        color: theme.palette.text.primary,
        fontFamily: 'Montserrat',
        fontSize: '16px',
        fontWeight: 500,
        '::placeholder': {
          color: theme.palette.text.disabled,
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };

  const pms = [
    {
      pmType: EPaymentMethod.INVOICE,
      title: getPaymentTypeString(EPaymentMethod.INVOICE),
      description: 'Billed monthly and prorated for all new products.',
    },
    {
      pmType: EPaymentMethod.CARD,
      title: getPaymentTypeString(EPaymentMethod.CARD),
      description: 'One time payments when you add new products.',
    },
  ];

  const selectPaymentMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
    const paymentMethod = e.target.value as EPaymentMethod;
    formManager.setFieldValue('paymentMethod', paymentMethod);

    if (paymentMethod === EPaymentMethod.INVOICE) {
      setShowCardElement(false);
    }
  };

  return (
    <form onSubmit={formManager.handleSubmit} autoComplete="off">
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FormControl
            fullWidth
            sx={{
              paddingBottom: 2,
            }}
          >
            <RadioGroup
              row
              defaultValue={EPaymentMethod.CARD}
              value={formManager.values.paymentMethod}
              onChange={selectPaymentMethod}
              sx={{
                flexDirection: 'row',
              }}
              name="paymentMethod"
            >
              {pms.map((pm, index) => (
                <Box
                  key={`peyment-type-${index}`}
                  sx={{
                    width: '50%',
                  }}
                >
                  <FormControlLabel
                    control={<Radio value={pm.pmType} />}
                    label={
                      <Box
                        sx={{
                          paddingY: '9px',
                        }}
                      >
                        <Typography variant="body2" color="primary">
                          {pm.title}
                        </Typography>

                        <Typography
                          color="textSecondary"
                          sx={{
                            fontSize: 10,
                            lineHeight: 1.3,
                            marginTop: 0.5,
                            maxWidth: 120,
                          }}
                        >
                          {pm.description}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      alignItems: 'flex-start',
                    }}
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={
              formManager.values.paymentMethod === EPaymentMethod.INVOICE
                ? 'Your Name'
                : formManager.values.paymentMethod === EPaymentMethod.CARD
                ? 'Name on Card'
                : ''
            }
            variant="outlined"
            value={formManager.values.name}
            onChange={formManager.handleChange('name')}
            error={!!(formManager.errors.name && formManager.touched.name)}
            helperText={formManager.touched.name ? formManager.errors.name : null}
          />
        </Grid>

        {formManager.values.paymentMethod === EPaymentMethod.INVOICE && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              value={formManager.values.email}
              onChange={formManager.handleChange('email')}
              error={!!(formManager.errors.email && formManager.touched.email)}
              helperText={formManager.touched.email ? formManager.errors.email : null}
            />
          </Grid>
        )}

        {formManager.values.paymentMethod === EPaymentMethod.CARD &&
          (showCardElement ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  position: 'relative',
                }}
              >
                <TextField
                  fullWidth
                  label="Payment"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!paymentElementError}
                  helperText={paymentElementError}
                  disabled
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    paddingY: '16px',
                    paddingX: '14px',
                  }}
                >
                  <CardElement id="cc-number" options={CARD_ELEMENT_OPTIONS} />
                </Box>
                <Button onClick={() => setShowCardElement(false)}>cancel</Button>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Alert
                icon={
                  <CreditCard
                    fontSize="inherit"
                    sx={{
                      fill: theme.palette.primary.main,
                    }}
                  />
                }
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setShowCardElement(true);
                    }}
                    sx={{
                      paddingX: 2,
                      marginTop: '-4px',
                    }}
                  >
                    CHANGE
                  </Button>
                }
                sx={{
                  boxShadow: 'none',
                  height: 56,
                  alignItems: 'center',
                  backgroundColor: `${theme.palette.primary.main}20`,
                  color: theme.palette.primary.main,
                }}
              >
                Card on file ending in {formManager.values.cardLastFourCode}.
              </Alert>
            </Grid>
          ))}

        <Grid item xs={12} hidden>
          <Button
            variant="contained"
            type="submit"
            onClick={() => formManager.handleSubmit()}
            endIcon={
              isUpdating ? (
                <CircularProgress
                  color="inherit"
                  size={14}
                  sx={{
                    marginLeft: 0.5,
                  }}
                />
              ) : null
            }
            sx={{
              paddingX: 2.5,
            }}
          >
            Update Billing
          </Button>
        </Grid>
      </Grid>
    </form>
  );
});
